import { connectToParent } from "penpal";
import type { OpenOSContext } from "ic-analytics";

interface OSApi {
    getAccessToken: () => Promise<string>;
}

const HANDSHAKE_TIMEOUT = 1500;
const CONTEXT_WAIT = 2000;
const CONTEXT_ATTEMPTS = 3;

let contextHandler: ((osContext: OpenOSContext) => void) | undefined;
let onContextReceived: (() => void) | undefined;

// Every connection advertises receiveOsContext, including the token-only ones.
// WPP Open pushes the project onto whichever child it last shook hands with, and a
// connection that advertises no methods makes that push throw
// "receiveOsContext is not a function" in the parent frame — losing the project.
function connect() {
    return connectToParent<{ osApi: OSApi }>({
        timeout: HANDSHAKE_TIMEOUT,
        methods: {
            receiveOsContext: (osContext: OpenOSContext) => {
                contextHandler?.(osContext);
                onContextReceived?.();
            }
        }
    });
}

// penpal types destroy as a bare Function, so narrow it here rather than at each call.
const closeConnection = (connection: { destroy: unknown }): void => {
    (connection.destroy as () => void)();
};

const waitForContext = (): Promise<boolean> =>
    new Promise(resolve => {
        const timer = setTimeout(() => {
            onContextReceived = undefined;
            resolve(false);
        }, CONTEXT_WAIT);

        onContextReceived = () => {
            clearTimeout(timer);
            onContextReceived = undefined;
            resolve(true);
        };
    });

// The handshake only proves the parent is reachable; the context arrives separately and
// can be dropped. Re-handshake a few times so the parent re-binds to a child that can
// receive it. Runs in the background: login must not wait on the context.
async function retryUntilContextArrives(received: () => boolean) {
    for (let attempt = 1; attempt < CONTEXT_ATTEMPTS && !received(); attempt += 1) {
        const connection = connect();

        try {
            await connection.promise;
        } catch {
            closeConnection(connection);
            continue;
        }

        if (await waitForContext()) {
            return;
        }

        closeConnection(connection);
    }
}

export async function getWppToken() {
    const connection = connect();

    const { osApi } = await connection.promise;

    return osApi.getAccessToken();
}

export async function getWppContext(handleWppContext: (osContext: OpenOSContext) => void) {
    let received = false;

    contextHandler = osContext => {
        received = true;
        handleWppContext(osContext);
    };

    const connection = connect();

    try {
        await connection.promise;
    } catch (error) {
        console.error("Failed to get WPP context:", error);
        throw error;
    }

    // Deliberately not awaited: resolve as soon as the handshake lands so authorization
    // stays as fast as before, and keep chasing the context afterwards.
    void (async () => {
        if (await waitForContext()) {
            return;
        }

        await retryUntilContextArrives(() => received);

        if (!received) {
            console.error(
                "WPP Open never delivered the project context; content stays scoped to the last known project"
            );
        }
    })();
}
